import React, { useState } from 'react'
import { Text, TextInput, TextStyle, View } from 'react-native'

interface InlineTextInputProps {
  value: string
  onChangeText: (value: string) => void
  onFocus?: () => void
  placeholder?: string
  // Typography of the rendered text, so editing looks like the finished page
  textStyle: TextStyle
  accessibilityLabel?: string
}

/**
 * A borderless, auto-growing, single-paragraph text input styled exactly like
 * the text it edits. Line breaks are stripped: headings don't hold newlines.
 *
 * Height comes from a hidden copy of the text with the same font and width,
 * not from the input's content size: on web that's the textarea's
 * scrollHeight, which never reports less than the current height, so an input
 * measured while narrow (e.g. before layout settles) could never shrink back.
 */
const InlineTextInput = ({
  value,
  onChangeText,
  onFocus,
  placeholder,
  textStyle,
  accessibilityLabel
}: InlineTextInputProps) => {
  const lineHeight = Number(textStyle.lineHeight ?? textStyle.fontSize ?? 16)
  const [height, setHeight] = useState(lineHeight)
  const { marginBottom, ...fontStyle } = textStyle

  return (
    <View style={{ marginBottom }}>
      <Text
        aria-hidden
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        onLayout={({ nativeEvent }) =>
          setHeight(Math.max(lineHeight, Math.ceil(nativeEvent.layout.height)))
        }
        style={[
          fontStyle,
          {
            position: 'absolute',
            left: 0,
            right: 0,
            opacity: 0,
            // web only: keep the measuring copy out of the way of clicks
            pointerEvents: 'none'
          } as TextStyle
        ]}
      >
        {/* A trailing space keeps an empty or space-ending value one line tall */}
        {`${value || placeholder || ''} `}
      </Text>
      <TextInput
        multiline
        value={value}
        placeholder={placeholder}
        // --w50-ink-400
        placeholderTextColor="#AFAEAD"
        accessibilityLabel={accessibilityLabel}
        onFocus={onFocus}
        onChangeText={text => onChangeText(text.replace(/[\r\n]+/g, ' '))}
        style={[
          fontStyle,
          {
            height,
            padding: 0,
            borderWidth: 0,
            backgroundColor: 'transparent',
            // The measured height is authoritative; never scroll. (Chrome can
            // report a textarea's content a few px taller than its line
            // height, which shows a scroll bar when scroll bars are visible.)
            overflow: 'hidden',
            // web only: hide the focus ring, the block frame shows selection
            outlineWidth: 0
          } as TextStyle
        ]}
      />
    </View>
  )
}

export default InlineTextInput
